import {CM_LOGGER_RENDERING} from "@app/common/services/logging"
import {Component, ElementRef, inject, input, OnDestroy, OnInit, output, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {HalPainterImageFinalConversion} from "@app/common/components/canvas/webgl2-canvas/hal-painter-image-final-conversion"
import {HalContext, HalPaintable} from "@cm/hal"
import {Vector2} from "@cm/math"
import {WebGl2Context, WebGl2CreateArgs, WebGl2PaintableCanvas} from "@cm/webgl2"
import {HalImageRef} from "@cm/hal/hal-ref"

const WAIT_FOR_FLUSH_COMPLETION = false // this is useful for profiling but might have an performance impact
const MAX_WIDTH = 3840 // 4K resolution
const MAX_HEIGHT = 2160 // 4K resolution

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-webgl2-canvas",
    templateUrl: "./webgl2-canvas.component.html",
    styleUrls: ["./webgl2-canvas.component.scss"],
})
export class WebGl2CanvasComponent implements OnInit, OnDestroy {
    private readonly logger = inject(CM_LOGGER_RENDERING)

    readonly aboutToBeDestroyed = output<void>()

    readonly $webGlCanvasElementRef = viewChild.required<ElementRef>("webGlCanvasElement")

    readonly $webglSettings = input<WebGl2CreateArgs>(
        {
            physicalCache: {
                sizeInBytes: 2 * 1024 * 1024 * 1024, // 2GB physical cache size
            },
            virtualCache: undefined, // disabled virtualization
        },
        {alias: "webglSettings"},
    )
    readonly resized = output<Vector2>()

    get devicePixelRatio(): number {
        return this._devicePixelRatio
    }

    // OnInit
    ngOnInit() {
        const canvas = this.$webGlCanvasElementRef().nativeElement
        if (!canvas) {
            throw Error("Canvas not found")
        }
        const halContext = WebGl2Context.createFromCanvas(canvas, this.$webglSettings())

        const paintableCanvas = new WebGl2PaintableCanvas(halContext, canvas)
        const backBufferImage = halContext.createPhysicalImage({
            width: 256,
            height: 256,
            channelLayout: "RGBA",
            dataType: "uint8norm_srgb",
        })
        const painterImageFinalConversion = HalPainterImageFinalConversion.create(halContext)

        const resizeObserver = new ResizeObserver(async (entries) => {
            const entry = entries.find((entry) => entry.target === this.canvas)
            if (!entry) {
                throw Error("ResizeObserver: entry not found")
            }
            let width: number
            let height: number
            if (entry.devicePixelContentBoxSize) {
                width = Math.round(entry.devicePixelContentBoxSize[0].inlineSize)
                height = Math.round(entry.devicePixelContentBoxSize[0].blockSize)
            } else {
                width = Math.round(entry.contentRect.width * devicePixelRatio)
                height = Math.round(entry.contentRect.height * devicePixelRatio)
            }
            // TODO on macOS the reported resolution is sometimes altered by the OS so that the devicePixelRatio is integer to avoid blurry rendering
            // TODO though this can cause excessive over-sampling resolution so the max resolution is limited to 4K (in webgl2-canvas.component.ts)
            // TODO however when mapping the view coordinates through the inverse view transform, it is still too large i think - to be further investigated !
            this._devicePixelRatio = window.devicePixelRatio
            if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                this.logger.warn(`Canvas (${width}x${height}) size exceeds maximum allowed size (${MAX_WIDTH}x${MAX_HEIGHT}).`)
                const scalingFactor = Math.max(width / MAX_WIDTH, height / MAX_HEIGHT)
                this._devicePixelRatio /= scalingFactor
                width = Math.round(width / scalingFactor)
                height = Math.round(height / scalingFactor)
                this.logger.warn(`Clamping canvas size to (${width}x${height}).`)
            }
            await this.resizeCanvasToDisplaySize(width, height)
            this.resized.emit(new Vector2(width, height))
        })
        try {
            resizeObserver.observe(canvas, {box: "device-pixel-content-box"})
        } catch (_error: unknown) {
            this.logger.warn("'device-pixel-content-box' feature not supported; try the established way (which may lead to blurry images)")
            resizeObserver.observe(canvas, {box: "content-box"})
        }
        this._data = {
            canvas,
            halContext,
            resizeObserver,
            paintableCanvas,
            backBufferImage,
            painterImageFinalConversion,
        }
    }

    // OnDestroy
    ngOnDestroy(): void {
        if (!this._data) {
            throw Error("WebGl2CanvasComponent: Not initialized")
        }
        this.aboutToBeDestroyed.emit()

        this._data.resizeObserver.disconnect()
        this._data.backBufferImage.release()
        this._data.painterImageFinalConversion.dispose()
        this._data.paintableCanvas.dispose()
        this._data.halContext.dispose()
        this._data = null
    }

    get canvas(): HTMLCanvasElement {
        if (!this._data) {
            throw Error("WebGlCanvas not initialized")
        }
        return this._data.canvas
    }

    get halContext(): HalContext {
        if (!this._data) {
            throw Error("WebGlCanvas not initialized")
        }
        return this._data.halContext
    }

    get backBufferImage(): HalImageRef {
        if (!this._data) {
            throw Error("WebGlCanvas not initialized")
        }
        return this._data.backBufferImage
    }

    clearBackBuffer(): void {
        this.backBufferImage.ref.clear({
            color: {r: 0, g: 0, b: 0, a: 0},
        })
    }

    async presentBackBuffer(convertToSRGB: boolean, gamma: number): Promise<void> {
        if (!this._data) {
            throw Error("WebGlCanvas not initialized")
        }
        // blit the sRgb image to the canvas
        this._data.painterImageFinalConversion.convertToSrgb = convertToSRGB
        this._data.painterImageFinalConversion.gamma = gamma
        this._data.painterImageFinalConversion.paint(this._data.paintableCanvas, this._data.backBufferImage.ref)
        if (WAIT_FOR_FLUSH_COMPLETION) {
            const data = this._data
            await data.halContext.flush(true)
        } else {
            await this._data.halContext.flush(false)
        }
    }

    private async resizeCanvasToDisplaySize(displayWidth: number, displayHeight: number) {
        if (!this._data) {
            throw Error("WebGlCanvas not initialized")
        }
        const needResize = this._allocatedSize.x !== displayWidth || this._allocatedSize.y !== displayHeight
        if (!needResize) {
            return
        }
        this._allocatedSize = new Vector2(displayWidth, displayHeight)
        this.canvas.width = displayWidth
        this.canvas.height = displayHeight
        this._data.backBufferImage.release()
        this._data.backBufferImage = this.halContext.createPhysicalImage({
            width: displayWidth,
            height: displayHeight,
            channelLayout: "RGBA",
            dataType: "uint8norm_srgb",
        })
        this.logger.debug(`Resized canvas to ${displayWidth}x${displayHeight}`)
    }

    private _data: Data | null = null
    private _allocatedSize = new Vector2(0, 0)
    private _devicePixelRatio = window.devicePixelRatio || 1
}

type Data = {
    canvas: HTMLCanvasElement
    halContext: HalContext
    resizeObserver: ResizeObserver
    // these are the gl elements for the linear color space framebuffer (the main render target)
    paintableCanvas: HalPaintable
    backBufferImage: HalImageRef
    painterImageFinalConversion: HalPainterImageFinalConversion
}
